/**
 * Настройки → Пользователи (ТЗ §8.7): список, создание, деактивация, сброс пароля,
 * привязка MAX.
 *
 * Данными владеет SettingsPage: сюда приходят готовые списки и три обработчика.
 *
 * Про MAX: контракт (`docs/spec/_api-contract.md`, раздел 1) знает единственную ручку
 * `POST /api/me/max-link` — код выдаётся тому, кто вошёл. Ручки «код за другого пользователя»
 * в API нет, поэтому кнопка привязывает учётную запись текущего главврача, а по остальным
 * в строке показано, привязан у них MAX или нет.
 */

import { useState, type FormEvent } from 'react';
import type {
    CreateUserRequest,
    DepartmentDto,
    MaxLinkResponse,
    Role,
    UpdateUserRequest,
    UserDto,
} from '@/api/types';
import { errorMessage, FormError, LoadingState } from '@/components/ErrorState';
import { ROLE_LABELS } from '@/lib/stages';

export interface UsersTabProps {
    users: UserDto[];
    departments: DepartmentDto[];
    loading?: boolean;
    error?: unknown;
    onCreate: (input: CreateUserRequest) => Promise<unknown>;
    onUpdate: (id: string, patch: UpdateUserRequest) => Promise<unknown>;
    onMaxLink: () => Promise<MaxLinkResponse>;
}

type Mode =
    | { kind: 'none' }
    | { kind: 'create' }
    | { kind: 'edit'; id: string }
    | { kind: 'password'; id: string };

const EMPTY_FORM = {
    login: '',
    password: '',
    role: 'executor' as Role,
    full_name: '',
    post: '',
    department_id: '',
};

const MIN_PASSWORD = 8;

export default function UsersTab({
    users,
    departments,
    loading,
    error,
    onCreate,
    onUpdate,
    onMaxLink,
}: UsersTabProps) {
    const [mode, setMode] = useState<Mode>({ kind: 'none' });
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [maxLink, setMaxLink] = useState<MaxLinkResponse | null>(null);
    const [maxError, setMaxError] = useState<string | null>(null);

    const openCreate = () => {
        setForm({ ...EMPTY_FORM });
        setFormError(null);
        setMode({ kind: 'create' });
    };

    const openEdit = (user: UserDto) => {
        setForm({
            login: user.login,
            password: '',
            role: user.role,
            full_name: user.full_name,
            post: user.post ?? '',
            department_id: user.department_id ?? '',
        });
        setFormError(null);
        setMode({ kind: 'edit', id: user.id });
    };

    const openPassword = (user: UserDto) => {
        setForm({ ...EMPTY_FORM, login: user.login, full_name: user.full_name });
        setFormError(null);
        setMode({ kind: 'password', id: user.id });
    };

    const close = () => {
        setMode({ kind: 'none' });
        setFormError(null);
    };

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setFormError(null);
        try {
            await action();
            close();
        } catch (cause) {
            setFormError(errorMessage(cause));
        } finally {
            setBusy(false);
        }
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (busy) return;

        if (mode.kind === 'create') {
            const login = form.login.trim();
            if (!login || /\s/.test(login)) {
                setFormError('Логин обязателен и не должен содержать пробелов.');
                return;
            }
            if (form.password.length < MIN_PASSWORD) {
                setFormError(`Пароль должен быть не короче ${MIN_PASSWORD} символов.`);
                return;
            }
            if (!form.full_name.trim()) {
                setFormError('Укажите ФИО пользователя.');
                return;
            }
            void run(() =>
                onCreate({
                    login,
                    password: form.password,
                    role: form.role,
                    full_name: form.full_name.trim(),
                    post: form.post.trim() || undefined,
                    department_id: form.department_id || null,
                }),
            );
            return;
        }

        if (mode.kind === 'edit') {
            if (!form.full_name.trim()) {
                setFormError('Укажите ФИО пользователя.');
                return;
            }
            const id = mode.id;
            void run(() =>
                onUpdate(id, {
                    full_name: form.full_name.trim(),
                    // Очищенное поле — это `null`, а не пустая строка: в БД должно
                    // остаться NULL, как при создании пользователя без должности.
                    post: form.post.trim() || null,
                    department_id: form.department_id || null,
                    role: form.role,
                }),
            );
            return;
        }

        if (mode.kind === 'password') {
            if (form.password.length < MIN_PASSWORD) {
                setFormError(`Пароль должен быть не короче ${MIN_PASSWORD} символов.`);
                return;
            }
            const id = mode.id;
            void run(() => onUpdate(id, { password: form.password }));
        }
    };

    const toggleActive = (user: UserDto) => {
        void run(() => onUpdate(user.id, { is_active: !user.is_active }));
    };

    const requestMaxLink = async () => {
        setMaxError(null);
        try {
            setMaxLink(await onMaxLink());
        } catch (cause) {
            setMaxLink(null);
            setMaxError(errorMessage(cause));
        }
    };

    const needle = search.trim().toLowerCase();
    const visible = needle
        ? users.filter((user) =>
              [user.full_name, user.login, user.post ?? '', user.department_name ?? '']
                  .join(' ')
                  .toLowerCase()
                  .includes(needle),
          )
        : users;

    return (
        <>
            <div className="filter-bar">
                <input
                    type="search"
                    className="search-input"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Поиск по ФИО, логину, должности"
                    aria-label="Поиск по пользователям"
                />
                <button type="button" className="act-btn primary" onClick={openCreate}>
                    ＋ Новый пользователь
                </button>
                <button type="button" className="act-btn" onClick={() => void requestMaxLink()}>
                    Привязать MAX
                </button>
            </div>

            <FormError>{maxError}</FormError>
            {maxLink ? (
                <div className="modal-note">
                    Код привязки для вашей учётной записи: <b>{maxLink.code}</b>. {maxLink.instructions} После
                    привязки уведомления о назначениях, сроках и согласованиях будут приходить в MAX. Остальные
                    работники получают свой код у себя в профиле.
                </div>
            ) : null}

            {mode.kind !== 'none' ? (
                <div className="detail-box">
                    <div className="detail-title">
                        <span>
                            {mode.kind === 'create'
                                ? 'Новый пользователь'
                                : mode.kind === 'edit'
                                  ? 'Изменение пользователя'
                                  : 'Сброс пароля'}
                        </span>
                        <span className="period-note">{form.login || 'логин будет задан ниже'}</span>
                    </div>

                    <form onSubmit={onSubmit}>
                        <div className="kv">
                            {mode.kind === 'create' ? (
                                <>
                                    <span className="k">Логин</span>
                                    <span className="v">
                                        <input
                                            type="text"
                                            className="search-input"
                                            size={24}
                                            value={form.login}
                                            autoComplete="off"
                                            onChange={(event) =>
                                                setForm({ ...form, login: event.target.value })
                                            }
                                            aria-label="Логин"
                                        />
                                    </span>
                                </>
                            ) : null}

                            {mode.kind === 'create' || mode.kind === 'password' ? (
                                <>
                                    <span className="k">Пароль</span>
                                    <span className="v">
                                        <input
                                            type="text"
                                            className="search-input"
                                            size={24}
                                            value={form.password}
                                            autoComplete="new-password"
                                            onChange={(event) =>
                                                setForm({ ...form, password: event.target.value })
                                            }
                                            aria-label="Пароль"
                                        />
                                    </span>
                                </>
                            ) : null}

                            {mode.kind !== 'password' ? (
                                <>
                                    <span className="k">ФИО</span>
                                    <span className="v">
                                        <input
                                            type="text"
                                            className="search-input"
                                            size={32}
                                            value={form.full_name}
                                            onChange={(event) =>
                                                setForm({ ...form, full_name: event.target.value })
                                            }
                                            aria-label="ФИО"
                                        />
                                    </span>

                                    <span className="k">Должность</span>
                                    <span className="v">
                                        <input
                                            type="text"
                                            className="search-input"
                                            size={32}
                                            value={form.post}
                                            placeholder="Юрисконсульт, заместитель главного врача по клинико-экспертной работе"
                                            onChange={(event) => setForm({ ...form, post: event.target.value })}
                                            aria-label="Должность"
                                        />
                                    </span>

                                    <span className="k">Роль</span>
                                    <span className="v">
                                        <select
                                            className="act-select"
                                            value={form.role}
                                            onChange={(event) =>
                                                setForm({ ...form, role: event.target.value as Role })
                                            }
                                            aria-label="Роль"
                                        >
                                            <option value="executor">{ROLE_LABELS.executor}</option>
                                            <option value="chief">{ROLE_LABELS.chief}</option>
                                        </select>
                                    </span>

                                    <span className="k">Отделение</span>
                                    <span className="v">
                                        <select
                                            className="act-select"
                                            value={form.department_id}
                                            onChange={(event) =>
                                                setForm({ ...form, department_id: event.target.value })
                                            }
                                            aria-label="Отделение"
                                        >
                                            <option value="">— не задано</option>
                                            {departments.map((department) => (
                                                <option key={department.id} value={department.id}>
                                                    {department.name}
                                                </option>
                                            ))}
                                        </select>
                                    </span>
                                </>
                            ) : null}
                        </div>

                        <FormError>{formError}</FormError>

                        <div className="draft-bar">
                            <button type="submit" className="act-btn primary" disabled={busy}>
                                {busy ? 'Сохраняем…' : 'Сохранить'}
                            </button>
                            <button type="button" className="act-btn" disabled={busy} onClick={close}>
                                Отмена
                            </button>
                            {mode.kind === 'create' ? (
                                <span className="draft-note">
                                    Логин уникален для всей системы. Пароль сообщите работнику лично — панель
                                    его больше не покажет.
                                </span>
                            ) : null}
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="stat-block">
                <div className="block-header">
                    <span>👥 Пользователи клиники</span>
                    <span className="checklist-progress">
                        {visible.length} из {users.length}
                    </span>
                </div>
                <div className="block-body">
                    <FormError>{error ? errorMessage(error) : null}</FormError>
                    {loading ? <LoadingState variant="inline" label="Загружаем пользователей…" /> : null}
                    {!loading && visible.length === 0 ? (
                        <div className="list-empty">Пользователи не найдены.</div>
                    ) : null}

                    {visible.map((user) => (
                        <div className="check-item" key={user.id}>
                            <span className="check-src">{ROLE_LABELS[user.role]}</span>
                            <span className="check-text">
                                {user.full_name}
                                {user.is_active ? null : (
                                    <>
                                        {' '}
                                        <span className="chip bad">отключён</span>
                                    </>
                                )}
                                <br />
                                <span className="my-appl">
                                    {[
                                        user.login,
                                        user.post || null,
                                        user.department_name || null,
                                        user.max_linked === undefined
                                            ? null
                                            : user.max_linked
                                              ? 'MAX привязан'
                                              : 'MAX не привязан',
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </span>
                            </span>
                            <span className="review-wrap">
                                <button
                                    type="button"
                                    className="act-btn"
                                    disabled={busy}
                                    onClick={() => openEdit(user)}
                                >
                                    Изменить
                                </button>
                                <button
                                    type="button"
                                    className="act-btn"
                                    disabled={busy}
                                    onClick={() => openPassword(user)}
                                >
                                    Сбросить пароль
                                </button>
                                <button
                                    type="button"
                                    className="act-btn warn"
                                    disabled={busy}
                                    onClick={() => toggleActive(user)}
                                >
                                    {user.is_active ? 'Деактивировать' : 'Включить'}
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
                <div className="totals-row">
                    <span>
                        Активных: <b>{users.filter((user) => user.is_active).length}</b> · отключённых:{' '}
                        <b>{users.filter((user) => !user.is_active).length}</b>
                    </span>
                    <span>Удаление пользователей не предусмотрено: учётная запись деактивируется.</span>
                </div>
            </div>
        </>
    );
}
